import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import Icon from '../components/ui/Icon';
import { FadeInWhenVisible } from '../components/ui/Motion';
import { PRICING_PLANS } from '../data/tools';
import { authActions } from '../store';

const PricingPage = () => {
  const dispatch = useDispatch();
  const [annual, setAnnual] = useState(true);

  const faqs = [
    { q: 'What happens after my 5 free documents?', a: 'You can sign up for free to continue with limited features, or upgrade to Pro for unlimited access to all tools and features.' },
    { q: 'Can I cancel my subscription anytime?', a: 'Yes! You can cancel your subscription at any time. Your access continues until the end of your billing period.' },
    { q: 'Are my files secure?', a: 'All files are encrypted with AES-256 encryption during transfer and processing. Files are automatically deleted from our servers after 2 hours.' },
    { q: 'Do you offer team discounts?', a: 'Yes! Our Business plan includes volume discounts. Contact our sales team for custom pricing for teams of 10+.' },
    { q: 'What file formats do you support?', a: 'We support over 50 file formats including PDF, Word, Excel, PowerPoint, JPG, PNG, WEBP, SVG, HTML, and many more.' },
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge-brand mb-3 inline-block">Simple Pricing</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Start free, <span className="gradient-text">scale unlimited</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-lg max-w-xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            No credit card required. 5 free documents to get started.
          </motion.p>

          {/* Billing Toggle */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="flex items-center justify-center gap-3 mt-8">
            <span className={`text-sm font-medium ${!annual ? 'font-bold' : ''}`}
              style={{ color: !annual ? 'var(--text-primary)' : 'var(--text-muted)' }}>Monthly</span>
            <button onClick={() => setAnnual(!annual)}
              className="w-12 h-6 rounded-full relative transition-colors"
              style={{ background: annual ? '#3374ff' : 'var(--border-light)' }}>
              <div className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform"
                style={{ left: annual ? '28px' : '4px' }} />
            </button>
            <span className={`text-sm font-medium ${annual ? 'font-bold' : ''}`}
              style={{ color: annual ? 'var(--text-primary)' : 'var(--text-muted)' }}>
              Annual <span className="badge-mint ml-1">Save 20%</span>
            </span>
          </motion.div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {PRICING_PLANS.map((plan, i) => {
            const price = annual && plan.price > 0 ? Math.round(plan.price * 0.8) : plan.price;
            return (
              <FadeInWhenVisible key={plan.id} delay={i * 0.1}>
                <div className={`card p-8 h-full flex flex-col ${plan.popular ? 'ring-2 relative' : ''}`}
                  style={plan.popular ? { borderColor: '#3374ff', boxShadow: '0 0 60px -12px rgba(51,116,255,0.2)' } : {}}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="badge-brand !px-4 !py-1.5 !text-xs shadow-lg">Most Popular</span>
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{plan.name}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{plan.desc}</p>
                  </div>
                  <div className="mb-8">
                    <span className="text-5xl font-bold" style={{ color: 'var(--text-primary)' }}>
                      {price === 0 ? '$0' : `$${price}`}
                    </span>
                    {price > 0 && (
                      <span className="text-sm ml-1" style={{ color: 'var(--text-muted)' }}>/{plan.period}</span>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Icon name="Check" size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#10b981' }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => plan.id === 'free' && dispatch(authActions.setShowAuthModal(true))}
                    className={plan.popular ? 'btn-primary w-full !py-3.5' : 'btn-secondary w-full !py-3.5'}>
                    {plan.cta}
                  </button>
                </div>
              </FadeInWhenVisible>
            );
          })}
        </div>

        {/* FAQs */}
        <FadeInWhenVisible>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8" style={{ color: 'var(--text-primary)' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="card overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left">
                    <span className="text-sm font-semibold pr-4" style={{ color: 'var(--text-primary)' }}>{faq.q}</span>
                    <Icon name={openFaq === i ? 'ChevronUp' : 'ChevronDown'} size={16}
                      style={{ color: 'var(--text-muted)' }} className="flex-shrink-0" />
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: openFaq === i ? 'auto' : 0, opacity: openFaq === i ? 1 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden">
                    <p className="px-5 pb-5 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                      {faq.a}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </FadeInWhenVisible>
      </div>
    </div>
  );
};

export default PricingPage;
